import { cn } from '@/lib/utils'
import { TrendingUp, TrendingDown, Minus, type LucideIcon } from 'lucide-react'

interface KpiCardProps {
  title: string
  value: string | number
  delta?: number
  format?: 'currency' | 'percent' | 'number' | 'decimal'
  invertDelta?: boolean // ACoS, CPC: negativ = gut
  icon?: LucideIcon
  loading?: boolean
}

function formatValue(value: string | number, format: string = 'number'): string {
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '—'
  switch (format) {
    case 'currency': return `${num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`
    case 'percent': return `${num.toFixed(1)}%`
    case 'decimal': return num.toFixed(2)
    case 'number': return num.toLocaleString('de-DE')
    default: return String(num)
  }
}

export default function KpiCard({ title, value, delta, format = 'number', invertDelta = false, icon: Icon, loading = false }: KpiCardProps) {
  if (loading) {
    return (
      <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-4 animate-pulse">
        <div className="h-3 w-20 bg-[var(--muted)] rounded mb-2" />
        <div className="h-7 w-16 bg-[var(--muted)] rounded" />
      </div>
    )
  }

  // Delta-Farbe: positiv = gruen, negativ = rot. Bei invertierten Metriken umkehren
  const isPositive = delta != null && delta > 0
  const isNegative = delta != null && delta < 0
  const deltaGood = invertDelta ? isNegative : isPositive
  const deltaBad = invertDelta ? isPositive : isNegative

  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-[var(--muted-foreground)]">{title}</p>
        {Icon && <Icon className="h-4 w-4 text-[var(--muted-foreground)]" />}
      </div>
      <p className="mt-1 text-2xl font-bold tracking-tight">{formatValue(value, format)}</p>
      {delta != null && (
        <div className={cn(
          'mt-1 flex items-center gap-1 text-xs font-medium',
          deltaGood && 'text-emerald-600',
          deltaBad && 'text-red-600',
          !deltaGood && !deltaBad && 'text-[var(--muted-foreground)]',
        )}>
          {isPositive && <TrendingUp className="h-3 w-3" />}
          {isNegative && <TrendingDown className="h-3 w-3" />}
          {!isPositive && !isNegative && <Minus className="h-3 w-3" />}
          <span>{delta > 0 ? '+' : ''}{delta.toFixed(1)}% vs. Vorperiode</span>
        </div>
      )}
    </div>
  )
}
